/*
 * Copyright(c) 1995 - 2020 T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, share, switchMap, take, takeUntil, throttleTime } from 'rxjs/operators';
import { SlideToggleDeterminateValueUtil, SlideToggleIndeterminateValueUtil, SlideToggleValue, SlideToggleValueUtil } from './slide-toggle-value.util';
import { getCoordinatesFromMoveEvent, getThumbPosition } from './slide-toggle.util';

const SLIDE_TOGGLE_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SlideToggleComponent),
    multi: true
};

/**
 * Represents a slidable "switch" toggle that can be moved between on and off or
 * on, off and neutral on a 3-state mode.
 *
 * <pre>
 * <common-slide-toggle>label</common-slide-toggle>
 * </pre>
 *
 * or 3-way Slider
 *
 * <pre>
 * <common-slide-toggle [indeterminate]=true>label</common-slide-toggle>
 * </pre>
 */
@Component({
    selector: 'common-slide-toggle',
    templateUrl: 'slide-toggle.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.tabindex]': 'disabled ? null : tabIndex',
        class: 'sppSlideToggle'
    },
    providers: [SLIDE_TOGGLE_CONTROL_VALUE_ACCESSOR]
})
export class SlideToggleComponent implements ControlValueAccessor, OnInit, OnDestroy {
    @Input() tabIndex = 0;

    @Input() disabled = false;

    @Input() indeterminate = false;

    value: SlideToggleValue = null;

    containerCssWidth$: Observable<string | null>;

    isDragActive$ = new BehaviorSubject<boolean>(false);

    @ViewChild('toggleBar', { static: true })
    private toggleBarElement: ElementRef<HTMLDivElement>;

    private click$ = new Subject<void>();

    private drag$ = new Subject<MouseEvent | TouchEvent>();

    private thumbPosition$: Observable<number>;

    private externalValue$ = new Subject<SlideToggleValue>();

    private ngUnsubscribe$ = new Subject<void>();

    private touch$ = new Subject<void>();

    constructor(
        private cd: ChangeDetectorRef,
        private valueDeterminateUtil: SlideToggleDeterminateValueUtil,
        private valueIndeterminateUtil: SlideToggleIndeterminateValueUtil
    ) {
        this.thumbPosition$ = this.getThumbPosition();
        this.containerCssWidth$ = this.getContainerCssWidth()
    }

    ngOnInit(): void {
        this.value = this.getValueUtil().getDefaultValue();
        this.click$.pipe(
            throttleTime(10),
            switchMap(() => this.isDragActive$.pipe(take(1))),
            filter(isActive => !isActive)
        )
            .subscribe(_ => {
                this.value = this.getValueUtil().toggleValue(this.value);
                this.externalValue$.next(this.value);
                this.cd.markForCheck();
            });

        const dragNotActive$ = this.isDragActive$.pipe(filter(active => !active));

        this.thumbPosition$.pipe(
            switchMap(pos => dragNotActive$.pipe(
                map(() => pos),
                take(1)
            )),
            map(pos => this.getValueUtil().getValueByPosition(pos))
        )
            .subscribe(value => {
                if (value === this.value) {
                    // distinctUntilChanged may not work here, because the value may be set on click (click$)
                    return;
                }
                this.value = value;
                this.externalValue$.next(this.value);
                this.cd.markForCheck();
            })
    }

    ngOnDestroy(): void {
        this.click$.complete();
        this.isDragActive$.complete();
        this.drag$.complete();
        this.touch$.complete();
        this.externalValue$.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    registerOnChange(fn: (v: SlideToggleValue) => void): void {
        this.externalValue$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(v => fn(v));
    }

    registerOnTouched(fn: () => void): void {
        this.touch$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(_ => fn());
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.disabled = true;
        }
        this.disabled = false;
    }

    writeValue(val: boolean | null): void {
        this.value = val;
        this.cd.markForCheck();
    }

    @HostListener('mouseup')
    @HostListener('touchend')
    onInteractionEnd(): void {
        this.click$.next();
    }

    @HostListener('mousedown')
    @HostListener('touchstart')
    onTouch(): void {
        this.touch$.next();
    }

    onDrag(mouseMove: MouseEvent | TouchEvent): void {
        this.drag$.next(mouseMove);
    }

    onDragEnd(): void {
        this.isDragActive$.next(false);
    }

    onDragStart(): void {
        this.isDragActive$.next(true);
    }

    @HostListener('keydown.arrowLeft', ['$event'])
    onArrowLeft(e: KeyboardEvent): void {
        e.preventDefault();
        this.value = this.getValueUtil().moveLeft(this.value);
        this.externalValue$.next(this.value);
        this.cd.markForCheck();
    }

    @HostListener('keydown.arrowRight', ['$event'])
    onArrowRight(e: KeyboardEvent): void {
        e.preventDefault();
        this.value = this.getValueUtil().moveRight(this.value);
        this.externalValue$.next(this.value);
        this.cd.markForCheck();
    }

    @HostListener('keydown.space', ['$event'])
    @HostListener('keydown.enter', ['$event'])
    onKeyboardToggle(e: KeyboardEvent): void {
        e.preventDefault();
        this.value = this.getValueUtil().toggleValue(this.value);
        this.externalValue$.next(this.value);
        this.cd.markForCheck();
    }

    private getValueUtil(): SlideToggleValueUtil {
        return this.indeterminate ? this.valueIndeterminateUtil : this.valueDeterminateUtil;
    }

    private getThumbPosition(): Observable<number> {
        return this.drag$.pipe(
            throttleTime(10),
            map(getCoordinatesFromMoveEvent),
            map(Math.round),
            distinctUntilChanged(),
            map(clientX => {
                const containerRect = this.toggleBarElement.nativeElement.getBoundingClientRect();
                return getThumbPosition(clientX, containerRect.left, containerRect.width);
            }),
            distinctUntilChanged(),
            share()
        )
    }

    private getContainerCssWidth(): Observable<string> {
        return this.isDragActive$.pipe(
            mergeMap(isActive => isActive ? this.thumbPosition$ : of(-1))
        )
            .pipe(
                map(thumbPosition => thumbPosition !== -1 ? thumbPosition + '%' : '')
            );
    }
}
